import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiMiddleware from "../../api/apiMiddleware";
import { syncSupplierUser } from "../../utils/supplierServices";

const getSupplierDetails = (user) =>
  user?.supplier_details || user?.supplierDetails || {};

const ProfileBankDetails = ({ profileData, onSuccess, onError }) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    bank_account_holder_name: "",
    bank_account_number: "",
    bank_name: "",
    bank_ifsc_code: "",
    gst_number: "",
  });

  useEffect(() => {
    if (profileData?.data?.user) {
      const details = getSupplierDetails(profileData.data.user);
      setFormData({
        bank_account_holder_name: details.bank_account_holder_name || "",
        bank_account_number: details.bank_account_number || "",
        bank_name: details.bank_name || "",
        bank_ifsc_code: details.bank_ifsc_code || "",
        gst_number: details.gst_number || "",
      });
    }
  }, [profileData]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiMiddleware.put("/supplier/profile", data);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.data?.user) {
        syncSupplierUser(data.data.user);
      }
      queryClient.invalidateQueries(["supplierProfile"]);
      onSuccess?.("Bank details updated successfully");
    },
    onError: (error) => {
      onError?.(error.response?.data?.message || "Failed to update bank details");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const gstNumber = formData.gst_number.trim().toUpperCase();

    updateMutation.mutate({
      bank_account_holder_name: formData.bank_account_holder_name,
      bank_account_number: formData.bank_account_number,
      bank_name: formData.bank_name,
      bank_ifsc_code: formData.bank_ifsc_code.toUpperCase(),
      gst_number: gstNumber || "",
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            Bank Account Information
          </h2>
          <p className="text-sm text-gray-500">
            Update your bank details for receiving payments from bookings.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Holder Name
          </label>
          <input
            type="text"
            name="bank_account_holder_name"
            value={formData.bank_account_holder_name}
            onChange={handleChange}
            placeholder="Name as per bank account"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name
          </label>
          <input
            type="text"
            name="bank_name"
            value={formData.bank_name}
            onChange={handleChange}
            placeholder="e.g. State Bank of India"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Number
          </label>
          <input
            type="text"
            name="bank_account_number"
            value={formData.bank_account_number}
            onChange={handleChange}
            placeholder="Enter account number"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IFSC Code
          </label>
          <input
            type="text"
            name="bank_ifsc_code"
            value={formData.bank_ifsc_code}
            onChange={handleChange}
            placeholder="e.g. SBIN0001234"
            maxLength={11}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary uppercase"
          />
          <p className="text-xs text-gray-500 mt-1">11-character IFSC code</p>
        </div>

        <div className="md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
            GST Information
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GST Number (GSTIN)
            </label>
            <input
              type="text"
              name="gst_number"
              value={formData.gst_number}
              onChange={handleChange}
              placeholder="e.g. 22AAAAA0000A1Z5"
              maxLength={15}
              className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary uppercase"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add or update your GST number here. Leave blank if not applicable.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {updateMutation.isPending ? "Saving..." : "Save Bank Details"}
        </button>
      </div>
    </form>
  );
};

export default ProfileBankDetails;
